import type { ReactNode } from 'react'
import type { OrderLine } from '../data/orders'
import { formatAmount, formatDate, formatEach, formatQuantity } from '../lib/format'
import { RawStatusBadge, StatusBadge, TypeTag } from './Badge'
import './LineCard.css'

/**
 * One order line, the same on order detail and the SKU page. The view
 * (order_lines_display) decides cancel_state, line_value, is_open,
 * is_past_promise and the effective ESL date; this only words them.
 *
 * Cancelled (decided 2026-11-02, quantities win):
 *  - cancel_state 'cancelled' (nothing shipped): greyed, Cancelled tag,
 *    not counted. Never hidden.
 *  - 'balance_cancelled', shipped < ordered: "Shipped n of m · balance
 *    cancelled", counted at shipped qty.
 *  - 'balance_cancelled', shipped = ordered: nothing was left to cancel, so
 *    a normal shipped line ("Shipped m of m", Complete), counted in full,
 *    no Cancelled tag. For all 150 such lines on 2026-11-02 the view's
 *    shipped x price equals extended_price, so the value is the full one.
 *
 * Returns: "Return of n · not received yet" while nothing has come back,
 * "Returned r of n" once some has. A $0 line reads "Included".
 *
 * Dates, open lines only (Jeff 10-29): "ESL promise: <date>"; "Updated:
 * <date>" when set (alone when there is no original); otherwise "Date
 * pending". Never a required, expedite or P21 header date.
 */
export function LineCard({ line, heading }: { line: OrderLine; heading: ReactNode }) {
  const shippedAll =
    line.cancelState === 'balance_cancelled' &&
    line.qtyShipped !== null &&
    line.qtyOrdered !== null &&
    line.qtyShipped === line.qtyOrdered
  const cancelled = line.cancelState === 'cancelled'
  const balanceCancelled = line.cancelState === 'balance_cancelled' && !shippedAll

  const ordered = line.qtyOrdered
  const shipped = line.qtyShipped ?? 0

  let qtyText: string
  if (line.isReturn && ordered !== null) {
    // Returns (JCP 2026-11-02): nothing received yet reads as pending, never
    // as "Returned"; once some has come back, "Returned r of n". A cancelled
    // return just names its size.
    const n = formatQuantity(Math.abs(ordered))
    if (cancelled) qtyText = `Return of ${n}`
    else if (shipped === 0) qtyText = `Return of ${n} \u00b7 not received yet`
    else qtyText = `Returned ${formatQuantity(Math.abs(shipped))} of ${n}`
  } else if (shipped > 0 && ordered !== null) {
    qtyText = `Shipped ${formatQuantity(shipped)} of ${formatQuantity(ordered)}`
  } else {
    qtyText = `Qty ${formatQuantity(ordered)}`
  }
  if (balanceCancelled) qtyText += ' · balance cancelled'

  // A $0 line on a live order is a kit component or no-charge item:
  // "Included", secondary text, no "$0.00 ea" / "Line value $0.00". Still
  // listed. A cancelled $0 line keeps the Cancelled treatment.
  const included = !cancelled && line.salesPrice === 0

  // A line finished by shipping (all or part, balance cancelled) reads as
  // Complete; P21 may still call it Cancelled.
  const statusBadge = cancelled ? (
    <TypeTag icon="cancelled" label="Cancelled" />
  ) : line.cancelState === 'balance_cancelled' ? (
    <RawStatusBadge status="Complete" />
  ) : (
    <RawStatusBadge status={line.lineStatus} />
  )

  return (
    <li className={`esl-line-card${cancelled ? ' esl-line-card--cancelled' : ''}`}>
      <div className="esl-line-card__head">
        {heading}
        <span className="esl-badges">
          {line.isReturn ? <TypeTag icon="return" label="Return" /> : null}
          {statusBadge}
        </span>
      </div>
      {line.itemDescription ? <p className="esl-line-card__desc">{line.itemDescription}</p> : null}
      <div className="esl-line-card__row">
        <span>{qtyText}</span>
        {included ? (
          <span className="esl-line-card__included">Included</span>
        ) : (
          <span>{formatEach(line.salesPrice)}</span>
        )}
      </div>
      {included ? null : (
        <div className="esl-line-card__row">
          <span>Line value</span>
          <span className="esl-line-card__value">
            {cancelled ? 'Not counted' : formatAmount(line.lineValue)}
          </span>
        </div>
      )}
      {line.isOpen ? <LineDates line={line} /> : null}
    </li>
  )
}

function LineDates({ line }: { line: OrderLine }) {
  const original = line.eslPromiseDate
  const updated = line.eslUpdatedPromiseDate
  if (!original && !updated) {
    return (
      <div className="esl-line-card__dates">
        <StatusBadge role="warning" icon="pending" label="Date pending" />
      </div>
    )
  }
  return (
    <div className="esl-line-card__dates">
      {original && !updated ? <span>ESL promise: {formatDate(original)}</span> : null}
      {original && updated ? (
        <span className="esl-line-card__superseded">ESL promise: {formatDate(original)}</span>
      ) : null}
      {updated ? <span>Updated: {formatDate(updated)}</span> : null}
      {line.isPastPromise ? <StatusBadge role="danger" icon="alert" label="Past ESL promise" /> : null}
    </div>
  )
}
